import { useEffect, useState } from 'react';
import { cn } from '@/lib/cn';

/*
  ─────────────────────────────────────────────────────────────────────────────
  The clock on the gate screen.

  Why a gate needs one
  ────────────────────
  Dismissal is a window, not an event. An officer standing at the gate is
  constantly answering "are we late?", "has the queue been moving?", and — when
  a parent arrives after the window — "what time is it actually?". They were
  reading that off a phone in their other hand, which is also the hand holding
  this screen.

  The seconds are not decoration. A clock without a moving second is
  indistinguishable from a screenshot of one, and this screen sits idle between
  children; the tick is how an officer knows the tablet has not frozen mid-queue
  with a child's details still on it.

  Whose clock this is
  ───────────────────
  The device's own, which is the one the officer and the parent are both
  looking at. The DATE a code belongs to is decided by the server, not here —
  see `getTodayWAT` in the API — so this is a wall clock, not an authority on
  which day a code is valid for.
  ─────────────────────────────────────────────────────────────────────────────
*/
export function GateClock({ className }: { className?: string }) {
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    /* Lined up with the next whole second, so the display changes when a
       wristwatch next to it does rather than drifting half a second off. */
    let interval: ReturnType<typeof setInterval>;
    const align = setTimeout(
      () => {
        setNow(new Date());
        interval = setInterval(() => setNow(new Date()), 1000);
      },
      1000 - (Date.now() % 1000),
    );
    return () => {
      clearTimeout(align);
      clearInterval(interval);
    };
  }, []);

  const weekday = now.toLocaleDateString('en-GB', { weekday: 'long' });
  const date = now.toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
  const hhmm = now.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });
  const seconds = now.toLocaleTimeString('en-GB', { second: '2-digit' }).padStart(2, '0');

  return (
    <div
      className={cn(
        'flex items-center justify-between gap-3 rounded-lg border border-border bg-card px-3.5 py-2.5',
        className,
      )}
    >
      <div className="min-w-0">
        <p className="text-[12.5px] font-bold leading-tight text-foreground">{weekday}</p>
        <p className="truncate text-[11px] leading-tight text-muted-foreground">{date}</p>
      </div>

      {/* Tabular figures, or the whole line jitters sideways once a second as
          the digit widths change — which is exactly the thing a person
          standing in the sun reads as a flicker. */}
      <p
        className="flex-shrink-0 font-mono text-[26px] font-bold leading-none tabular-nums tracking-tight text-foreground"
        aria-label={`Time ${hhmm}`}
      >
        {hhmm}
        <span className="ml-0.5 text-[15px] font-semibold text-muted-foreground">:{seconds}</span>
      </p>
    </div>
  );
}
